import { useState, useMemo, useEffect } from 'react';
import { Icon, Loading, Button, Popover2 } from '@evertrue/et-components';
import { FlexTable } from '@evertrue/sdk';
import EverTrue from 'app';
import TasksHeader from 'apps/tasks/components/tasks-header';
import useCadences from 'apps/cadences-v2/hooks/use-cadences';
import cadenceGqlCall from 'apps/cadences-v2/utils/cadence-gql-call';
import { CADENCE_STEP_STATS_QUERY } from 'apps/cadences-v2/utils/cadence-queries';
import { walkCadenceGraph } from 'apps/cadences-v2/utils/cadence-step-utils';
import FilterBar from 'apps/cadences-v2/components/filter-bar';
import Api from 'entities/helpers/api';
import { useEmailTemplatesProvider } from 'apps/outreach/email/templates/email-templates-context';
import EmailTemplateFormModal from 'apps/outreach/email/templates/components/email-template-form-modal';

/** Event types that are internal and should not appear as tasks. */
const HIDDEN_EVENT_TYPES = ['wait', 'condition', 'start', 'end', 'exit'];

/** Map timeframe filter value to API PerformanceTimeRange enum. */
const TIME_RANGE_MAP = {
  day: 'DAYS_1',
  week: 'DAYS_7',
  month: 'DAYS_30',
  year: 'DAYS_365',
  all: null,
};

/** Event type display labels. */
const EVENT_TYPE_LABELS = {
  email: 'Email',
  sms: 'SMS',
  schedule: 'Schedule',
};

/** Completion timeframe dropdown options. */
const TIMEFRAME_OPTIONS = [
  { label: 'All time', value: 'all' },
  { label: 'Last day', value: 'day' },
  { label: 'Last week', value: 'week' },
  { label: 'Last month', value: 'month' },
  { label: 'Last year', value: 'year' },
];

/** Cadence type filter options. */
const CADENCE_TYPE_OPTIONS = [
  { label: 'Cadence', value: 'cadence' },
  { label: 'Bulk Message', value: 'bulk_message' },
  { label: 'Prospect Plan', value: 'prospect_plan' },
];

/** Action type filter options. */
const ACTION_TYPE_OPTIONS = [
  { label: 'Email', value: 'email' },
  { label: 'SMS', value: 'sms' },
];

/** Map cadenceType to display label and CSS modifier. */
const CADENCE_TYPE_MAP = {
  cadence: { label: 'Cadence', modifier: 'cadence' },
  bulk_message: { label: 'Bulk Message', modifier: 'bulk' },
  prospect_plan: { label: 'Prospect Plan', modifier: 'prospect' },
};

/**
 * AutomatedTasksController — list view per NEXDEV-811.
 * Uses the standard filter bar pattern and FlexTable (same as manual tasks).
 */
const AutomatedTasksController = () => {
  const { cadences, loading: cadencesLoading, error } = useCadences();
  const { templates, setSelectedTemplate } = useEmailTemplatesProvider();

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Timeframe
  const [timeframe, setTimeframe] = useState(TIMEFRAME_OPTIONS[0]);

  // Filters — all always-visible per spec
  const [cadenceTypeFilter, setCadenceTypeFilter] = useState([]);
  const [actionTypeFilter, setActionTypeFilter] = useState([]);
  const [cadenceNameFilter, setCadenceNameFilter] = useState([]);

  // Step stats for recipient counts
  const [stepStatMap, setStepStatMap] = useState({});

  // Template edit modal state
  const [isTemplateFormOpen, setIsTemplateFormOpen] = useState(false);

  /** Build step-level rows from cadence events. */
  const currentUserId = EverTrue.store?.user?.get('id');

  const stepRows = useMemo(() => {
    const rows = [];
    cadences
      .filter((c) => c.status?.toLowerCase() !== 'draft')
      // Only show cadences where the current user is the owner
      .filter((c) => currentUserId && String(c.ownerUserId) === String(currentUserId))
      .forEach((cadence) => {
        // Use graph walk for branch-aware step numbering (3a, 3b)
        const graphResult = walkCadenceGraph(cadence.events || [], cadence.edges || []);
        const stepNumMap = {};
        const mapItems = (items) => {
          (items || []).forEach((item) => {
            if (item.type === 'step' && item.event?.id) {
              stepNumMap[String(item.event.id)] = { num: item.num, suffix: item.suffix || '' };
            }
            if (item.type === 'branch') {
              mapItems(item.trueBranch);
              mapItems(item.falseBranch);
            }
          });
        };
        mapItems(graphResult.items);

        // Build step numbering that excludes hidden types (start, end, wait, condition)
        const visibleEvents = (cadence.events || [])
          .filter((e) => !HIDDEN_EVENT_TYPES.includes((e.eventType || '').toLowerCase()))
          .sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0));

        visibleEvents.forEach((event, visibleIdx) => {
          const eventType = (event.eventType || '').toLowerCase();
          if (event.isAutomated === false) return;

          const config = event.eventConfig || {};
          const stepInfo = stepNumMap[String(event.id)];
          rows.push({
            id: `${cadence.id}-${event.id}`,
            cadenceId: cadence.id,
            cadenceName: cadence.name,
            cadenceType: cadence.cadenceType || 'cadence',
            eventId: event.id,
            step: event.stepLabel || (stepInfo ? `${stepInfo.num}${stepInfo.suffix}` : visibleIdx + 1),
            stepName: config.name || EVENT_TYPE_LABELS[eventType] || eventType,
            actionType: eventType,
            templateName: config.template_name || config.name || config.subject || '',
            templateId: config.email_template_id || event.emailTemplateId || null,
            recipientsInTimeframe: 0,
          });
        });
      });
    return rows;
  }, [cadences, currentUserId]);

  /** Fetch step stats to populate recipient counts, passing timeframe filter. */
  useEffect(() => {
    const cadenceIds = [...new Set(stepRows.map((r) => r.cadenceId))];
    if (cadenceIds.length === 0) return;

    let cancelled = false;
    const timeRange = TIME_RANGE_MAP[timeframe?.value] || null;

    const fetchStats = async () => {
      const map = {};
      const variables = timeRange ? { timeRange } : {};
      await Promise.all(
        cadenceIds.map(async (cadenceId) => {
          try {
            const data = await cadenceGqlCall(CADENCE_STEP_STATS_QUERY, {
              cadenceId: String(cadenceId),
              ...variables,
            });
            (data?.cadenceStepStats || []).forEach((ss) => {
              map[`${cadenceId}-${ss.cadenceEventId}`] = ss.emailsSent || ss.totalExecuted || 0;
            });
          } catch {
            // skip failed cadence
          }
        })
      );
      if (!cancelled) setStepStatMap(map);
    };

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [stepRows, timeframe]);

  /** Rows with recipient counts merged in. */
  const rowsWithCounts = useMemo(() => {
    return stepRows.map((row) => ({
      ...row,
      recipientsInTimeframe: stepStatMap[`${row.cadenceId}-${row.eventId}`] || 0,
    }));
  }, [stepRows, stepStatMap]);

  /** Cadence name options derived from data. */
  const cadenceNameOptions = useMemo(() => {
    const names = [...new Set(rowsWithCounts.map((r) => r.cadenceName))].sort();
    return names.map((n) => ({ label: n, value: n }));
  }, [rowsWithCounts]);

  /** Apply search and filters. Hide rows with 0 recipients. */
  const filteredRows = useMemo(() => {
    return rowsWithCounts.filter((row) => {
      // Only show rows with at least 1 recipient
      if (row.recipientsInTimeframe < 1) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchName = row.cadenceName?.toLowerCase().includes(q);
        const matchStep = row.stepName?.toLowerCase().includes(q);
        const matchTemplate = row.templateName?.toLowerCase().includes(q);
        if (!matchName && !matchStep && !matchTemplate) return false;
      }
      if (cadenceTypeFilter.length > 0 && !cadenceTypeFilter.some((f) => f.value === row.cadenceType)) return false;
      if (actionTypeFilter.length > 0 && !actionTypeFilter.some((f) => f.value === row.actionType)) return false;
      if (cadenceNameFilter.length > 0 && !cadenceNameFilter.some((f) => f.value === row.cadenceName)) return false;
      return true;
    });
  }, [rowsWithCounts, searchQuery, cadenceTypeFilter, actionTypeFilter, cadenceNameFilter]);

  const handleRowClick = (row) => {
    EverTrue.Navigator(`/tasks/automated/${row.cadenceId}-${row.eventId}`, true);
  };

  const handleViewCadence = (cadenceId, cadenceType) => {
    const path =
      cadenceType === 'bulk_message' ? `/cadences-v2/bulk-message/${cadenceId}` : `/cadences-v2/${cadenceId}`;
    EverTrue.Navigator(path, true);
  };

  /** Navigate to cadence detail page with step pre-selected and scrolled into view. */
  const handleViewStepDetails = (row) => {
    const path =
      row.cadenceType === 'bulk_message'
        ? `/cadences-v2/bulk-message/${row.cadenceId}`
        : `/cadences-v2/${row.cadenceId}?stepId=${row.eventId}`;
    EverTrue.Navigator(path, true);
  };

  /** Open the template edit modal for a given template ID. */
  const handleOpenTemplateEditor = async (templateId) => {
    if (!templateId) return;
    const local = templates.find((t) => String(t.id) === String(templateId));
    if (local) {
      setSelectedTemplate(local);
      setIsTemplateFormOpen(true);
      return;
    }
    try {
      const fetched = await Api.OUTREACH.EMAIL_TEMPLATE.get({ urlArgs: { id: templateId } });
      if (fetched) {
        setSelectedTemplate(fetched);
        setIsTemplateFormOpen(true);
      }
    } catch {
      EverTrue.Alert.error('Could not load email template.');
    }
  };

  /** Filter definitions for the shared FilterBar component. */
  const filterDefs = [
    {
      id: 'timeframe',
      label: 'Timeframe',
      type: 'select',
      options: TIMEFRAME_OPTIONS,
      value: timeframe,
      onChange: setTimeframe,
      alwaysVisible: true,
      alwaysShowLabel: true,
    },
    {
      id: 'cadenceType',
      label: 'Cadence Type',
      type: 'multiselect',
      options: CADENCE_TYPE_OPTIONS,
      value: cadenceTypeFilter,
      onChange: setCadenceTypeFilter,
      alwaysVisible: true,
      alwaysShowLabel: true,
    },
    {
      id: 'actionType',
      label: 'Action Type',
      type: 'multiselect',
      options: ACTION_TYPE_OPTIONS,
      value: actionTypeFilter,
      onChange: setActionTypeFilter,
      alwaysVisible: true,
      alwaysShowLabel: true,
    },
    {
      id: 'cadenceName',
      label: 'Cadence',
      type: 'multiselect',
      options: cadenceNameOptions,
      value: cadenceNameFilter,
      onChange: setCadenceNameFilter,
      alwaysVisible: true,
      alwaysShowLabel: true,
      searchable: true,
    },
  ];

  /** FlexTable column definitions. */
  const columns = useMemo(
    () => [
      {
        field: 'cadenceName',
        headerName: 'Cadence / Step',
        flex: 1.5,
        minWidth: 200,
        sortable: true,
        hideable: false,
        renderCell: ({ row }) => (
          <div className="automated-tasks--cadence-cell">
            <button
              type="button"
              className="automated-tasks--cadence-link"
              onClick={(e) => {
                e.stopPropagation();
                handleViewCadence(row.cadenceId, row.cadenceType);
              }}
            >
              {row.cadenceName}
            </button>
            <span className="automated-tasks--step-label">
              Step {row.step} | {row.stepName}
            </span>
          </div>
        ),
      },
      {
        field: 'cadenceType',
        headerName: 'Cadence Type',
        flex: 0.8,
        minWidth: 120,
        sortable: true,
        hideable: false,
        renderCell: ({ row }) => {
          const typeInfo = CADENCE_TYPE_MAP[row.cadenceType] || {
            label: row.cadenceType || 'Cadence',
            modifier: 'cadence',
          };
          return (
            <span className={`automated-tasks--cadence-type-badge automated-tasks--cadence-type-${typeInfo.modifier}`}>
              {typeInfo.label}
            </span>
          );
        },
      },
      {
        field: 'actionType',
        headerName: 'Action',
        flex: 0.6,
        minWidth: 100,
        sortable: true,
        hideable: false,
        renderCell: ({ row }) => {
          const isEmail = row.actionType === 'email';
          return (
            <span className={`automated-tasks--action-badge ${isEmail ? 'is-email' : 'is-sms'}`}>
              <Icon icon={isEmail ? 'email' : 'chat'} />
              {isEmail ? 'Email' : 'SMS'}
            </span>
          );
        },
      },
      {
        field: 'templateName',
        headerName: 'Template',
        flex: 1,
        minWidth: 140,
        sortable: true,
        hideable: false,
        renderCell: ({ row }) =>
          row.templateName && row.templateId ? (
            <button
              type="button"
              className="automated-tasks--template-link"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenTemplateEditor(row.templateId);
              }}
            >
              {row.templateName}
            </button>
          ) : (
            <span>{row.templateName || '\u2014'}</span>
          ),
      },
      {
        field: 'recipientsInTimeframe',
        headerName: 'Recipients in Timeframe',
        flex: 0.8,
        minWidth: 140,
        sortable: true,
        hideable: false,
        renderCell: ({ row }) => (
          <button
            type="button"
            className="automated-tasks--recipients-link"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(row);
            }}
          >
            {(row.recipientsInTimeframe || 0).toLocaleString()}
          </button>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 80,
        sortable: false,
        filterable: false,
        hideable: false,
        type: 'actions',
        renderCell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Popover2
              trigger={
                <Button type="simple" className="automated-tasks--action-btn">
                  <Icon icon="more-horiz" />
                </Button>
              }
            >
              {({ closePopover }) => (
                <div className="automated-tasks--action-menu">
                  {row.cadenceType === 'bulk_message' ? (
                    <button
                      type="button"
                      onClick={() => {
                        closePopover();
                        handleViewCadence(row.cadenceId, row.cadenceType);
                      }}
                    >
                      View Bulk Message Data
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          closePopover();
                          handleViewStepDetails(row);
                        }}
                      >
                        View Step Details
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          closePopover();
                          handleRowClick(row);
                        }}
                      >
                        View Recipients
                      </button>
                    </>
                  )}
                </div>
              )}
            </Popover2>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templates]
  );

  const hasFilters =
    searchQuery || cadenceTypeFilter.length > 0 || actionTypeFilter.length > 0 || cadenceNameFilter.length > 0;

  if (cadencesLoading) {
    return <Loading />;
  }

  return (
    <div className="automated-tasks-page">
      {/* Header — matches manual tasks page */}
      <TasksHeader taskType="automated" />

      {/* Filter bar — shared component, search on right */}
      <FilterBar
        filters={filterDefs}
        rightContent={
          <div className="automated-tasks-page--search-input">
            <Icon icon="search" />
            <input
              type="text"
              placeholder="Search cadence tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        }
      />

      {error && (
        <div className="automated-tasks-page--error">
          <Icon icon="error-outline" />
          <span>Failed to load tasks: {error}</span>
        </div>
      )}

      {/* Table */}
      <div className="automated-tasks-page--table-container">
        <div
          style={{
            height: 'calc(100vh - 320px)',
            width: '100%',
          }}
        >
          <FlexTable
            tableKey="automated-tasks-table"
            columns={columns}
            rows={filteredRows}
            getRowId={(params) => params.id}
            loading={cadencesLoading}
            onRowClick={(params) => handleRowClick(params.row)}
            disableRowSelectionOnClick
            disableColumnMenu={true}
            showColumnVerticalBorder={false}
            sortingMode="client"
            sortingOrder={['asc', 'desc']}
            paginationMode="client"
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
              sorting: { sortModel: [{ field: 'cadenceName', sort: 'asc' }] },
            }}
            getRowHeight={() => 'auto'}
            slots={{
              noRowsOverlay: () => (
                <div className="automated-tasks-page--empty">
                  <Icon icon="assignment" size={3} className="automated-tasks-page--empty-icon" />
                  <h3>No Automated Steps Found</h3>
                  <p>
                    {hasFilters
                      ? 'No tasks match your current filters.'
                      : 'Create cadences with automated steps to see them here.'}
                  </p>
                </div>
              ),
            }}
          />
        </div>
      </div>
      {isTemplateFormOpen && <EmailTemplateFormModal closeTemplateForm={() => setIsTemplateFormOpen(false)} />}
    </div>
  );
};

export default AutomatedTasksController;
